import { useState, useRef, useContext, useEffect } from "react";

import styles from "./MultitouchCalculatorComponent.module.css";

import { GlobalContext } from "../../../contexts/globalContext";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { byPrefixAndName } from '@awesome.me/kit-fe6b9b7683/icons';

import CustomMultitouchComponent from "../../../files/CustomMultitouchComponent";

import { evaluate } from 'mathjs';

// SVG icons
import back from '../../../graphics/conferenceScreen/Calculator/back.svg';
import divide from '../../../graphics/conferenceScreen/Calculator/divide.svg';
import equal from '../../../graphics/conferenceScreen/Calculator/equal.svg';
import minus from '../../../graphics/conferenceScreen/Calculator/minus.svg';
import multiply from '../../../graphics/conferenceScreen/Calculator/multiply.svg';
import plus from '../../../graphics/conferenceScreen/Calculator/plus.svg';
import divideInline from '../../../graphics/conferenceScreen/Calculator/divideInline.svg';
import minusInline from '../../../graphics/conferenceScreen/Calculator/minusInline.svg';
import multiplyInline from '../../../graphics/conferenceScreen/Calculator/multiplyInline.svg';
import plusInline from '../../../graphics/conferenceScreen/Calculator/plusInline.svg';

const MultitouchCalculatorComponent = ({ setOpenCalculator }) => {
    
    const { appWidth, appHeight } = useContext(GlobalContext);

    const mtRef = useRef(null);
    const currentDisplayRef = useRef();
    const isOpenCanvasRef = useRef(false);

    const [input, setInput] = useState("0");
    const [displayInput, setDisplayInput] = useState(["0"]);
    const [previous, setPrevious] = useState(null); 

    const options = {
        appWidth,
        appHeight,
        maxScale: 1.25,
        minScale: 0.5
    };

    const svgMap = {
        "÷": divide,
        "x": multiply,
        "-": minus,
        "+": plus,
        "=": equal,
        "⌫": back
    };

    const onMinScaleReached = () => {
        setOpenCalculator(false);
    };

    const handleButtonClick = (value) => {

        if (input.length >= 13) {
            return;
        }
        
        let actualValue = value;
        let displayElement;

        if (value === "x") {
            actualValue = "*";
            displayElement = <img src={multiplyInline} alt="×" className={styles.inlineSvg} />;
        } else if (value === "÷") {
            actualValue = "/";
            displayElement = <img src={divideInline} alt="÷" className={styles.inlineSvg} />;
        } else if (value === "+") {
            displayElement = <img src={plusInline} alt="+" className={styles.inlineSvg} />;
        } else if (value === "-") {
            displayElement = <img src={minusInline} alt="-" className={styles.inlineSvg} />;
        } else {
            displayElement = value;
        }

        // Check if value is operator and update 'previous'
        if (["+", "-", "x", "÷"].includes(value)) {
            setPrevious({ number: input, operator: value });
            setInput("0");
            setDisplayInput(["0"]);
            return;
        }

        if (input === "0") {
            setInput(actualValue);
            setDisplayInput([displayElement]);
        } else {
            if (input.length !== 12) {
                setInput(prev => prev + actualValue);
                setDisplayInput(prev => [...(Array.isArray(prev) ? prev : [prev]), displayElement]);
            }
        }
    };

    const handleClear = () => {
        setInput("0");
        setDisplayInput(["0"]);
        setPrevious(null);
    };

    const handleDelete = () => {
        const newInput = input.length > 1 ? input.slice(0, -1) : "0";
        const newDisplayInput = displayInput.length > 1 ? displayInput.slice(0, -1) : ["0"];
        setInput(newInput);
        setDisplayInput(newDisplayInput);
    };

    const handleCalculate = () => {
        try {
            let expression = input;
            if (previous) {
                const op = previous.operator === "x" ? "*" : previous.operator === "÷" ? "/" : previous.operator;
                expression = `${previous.number}${op}${input}`;
            }

            const result = evaluate(expression);
            const formatted = parseFloat(parseFloat(result).toFixed(10)).toString();
            setInput(formatted);
            setDisplayInput([formatted]);
            setPrevious(null); // Clear after calculation
        } catch (error) {
            setInput("Error");
            setDisplayInput(["Error"]);
            setPrevious(null);
        }
    };

    const handlePercentage = () => {
        try {
            const result = parseFloat(input) / 100;
            const formatted = parseFloat(parseFloat(result).toFixed(10)).toString();
            setInput(formatted);
            setDisplayInput([formatted]);
        } catch (error) {
            setInput("Error");
            setDisplayInput(["Error"]);
        }
    };

    const handleToggleSign = () => {
        try {
            const result = parseFloat(input) * -1;
            const formatted = parseFloat(parseFloat(result).toFixed(10)).toString();
            setInput(formatted);
            setDisplayInput([formatted]);
        } catch (error) {
            setInput("Error");
            setDisplayInput(["Error"]);
        }
    };

    useEffect(() => {
        const handleKeyPress = (event) => {
            const key = event.key;
            if (key === "Backspace") {
                handleDelete();
            } else if (key === "Enter") {
                handleCalculate();
            } else if (!isNaN(key) || ['+', '-', '*', '/', '.', '%'].includes(key)) {
                handleButtonClick(key);
            } else if (key === "Escape") {
                handleClear();
            }
        };

        window.addEventListener("keydown", handleKeyPress);
        return () => {
            window.removeEventListener("keydown", handleKeyPress);
        };
    }, [input, displayInput]);

    const adjustFontSize = () => {
        const element = currentDisplayRef.current;
        if (!element) return;
    
        const parentWidth = element.parentElement.offsetWidth;
        let fontSize = 48; // Start font size
        element.style.fontSize = `${fontSize}px`;
    
        // Small helper to measure content width
        const getContentWidth = () => {
            return Array.from(element.children)
                .reduce((total, child) => total + child.offsetWidth, 0);
        };
    
        let contentWidth = getContentWidth();
    
        // Avoid infinite loops: limit iterations
        const maxIterations = 20;
        let iteration = 0;
    
        // Only adjust if needed
        while (contentWidth > parentWidth-20 && fontSize > 16 && iteration < maxIterations) {
            fontSize -= 2; // Reduce slightly faster to limit loops
            element.style.fontSize = `${fontSize}px`;
            contentWidth = getContentWidth();
            iteration++;
        }
    };
    

    useEffect(() => {
        adjustFontSize();
    }, [displayInput]);

    return (
        <CustomMultitouchComponent
            ref={mtRef}
            dragGesture={false}
            x={1850} y={160}
            onMinScaleReached={onMinScaleReached}
            options={options}
            dragGestureMouse={true}
            dragBox={{x: 0, y: 0, width: 610, height: 100}}
            isOpenCanvasRef={isOpenCanvasRef}
            >
            <div className={styles.calculator}>
                <div className={styles.line}></div>
                <div className={styles.upDiv}>
                    <FontAwesomeIcon className={styles.icon} icon={byPrefixAndName.fal['calculator']} />
                    <label className={styles.calculatorLabel}>Calculator</label>
                    <FontAwesomeIcon className={styles.icon} onClick={() => setOpenCalculator(false)} icon={byPrefixAndName.fal['close']} />
                </div>
                <div className={styles.display}>
                    {previous && (
                        <div className={styles.previousDisplay}>
                            <span>{previous.number}</span>
                            <span style={{ marginLeft: 4 }}>{previous.operator}</span>
                        </div>
                    )}
                    <div className={styles.currentDisplay} ref={currentDisplayRef}>
                        {Array.isArray(displayInput)
                            ? displayInput.map((el, idx) => <span className={styles.value} key={idx}>{el}</span>)
                            : displayInput}
                    </div>
                </div>
                <div className={styles.buttons}>
                    {["AC", "+/-", "%", "÷", "7", "8", "9", "x", "4", "5", "6", "-", "1", "2", "3", "+", "0", ".", "⌫", "="].map((btn) => (
                        <button
                            key={btn}
                            className={`${styles.button} ${["+", "-", "x", "÷", "="].includes(btn) ? styles.basicOperator :
                                ["AC", "+/-", "%"].includes(btn) ? styles.specialFunction :
                                    btn === "=" ? styles.equalsButton :
                                        btn === "⌫" ? styles.backspaceButton :
                                            styles.button
                                }`}
                            onClick={() => {
                                if (btn === "AC") handleClear();
                                else if (btn === "⌫") handleDelete();
                                else if (btn === "%") handlePercentage();
                                else if (btn === "=") handleCalculate();
                                else if (btn === "+/-") handleToggleSign();
                                else handleButtonClick(btn);
                            }}
                        >
                            {svgMap[btn] ? (
                                <img src={svgMap[btn]} alt={btn} className={styles.svgIcon} />
                            ) : (
                                btn
                            )}
                        </button>
                    ))}
                </div>
            </div>
        </CustomMultitouchComponent>
    );
};

export default MultitouchCalculatorComponent;
